// A bill whose package misses required documents, in engineering's queue
// apps/smart-catalog/frontend/src/components/orchestrator/PackageGapsCard.tsx
//
// The item names the bill and how many items fail; the live report shows
// which, and what each misses. The item closes by itself once nothing fails,
// whether the documents arrived or the gaps were waived.

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { usePackageReport } from '../../hooks/usePackageVerification';
import { PACKAGE_TYPE_LABEL, requirementLabel } from '../../services/packageVerificationService';

/** Rows shown in the card before "open the bill" takes over. */
const SHOWN = 8;

export const PackageGapsCard: React.FC<{ item: { taskPayload?: Record<string, unknown> } }> = ({ item }) => {
  const navigate = useNavigate();
  const bomId = item.taskPayload?.bomId as string | undefined;
  // The first few failing parts; the counts cover the whole bill.
  const { data: report, isLoading } = usePackageReport(bomId, { filter: 'FAIL', uniqueParts: true, limit: SHOWN });

  if (isLoading) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  const failing = report?.lines ?? [];
  const failingTotal = report?.counts.fail ?? 0;

  return (
    <Box sx={{ py: 1.5, px: 1, maxWidth: 860 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{report?.bomName ?? 'Bill'}</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          {failingTotal} of {report?.counts.items ?? 0} items missing required documents
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {bomId && (
          <Button
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate(`/bom/${bomId}`, { state: { restoreTab: 'package' } })}
            sx={{ textTransform: 'none' }}
          >
            Open package
          </Button>
        )}
      </Stack>
      {failingTotal === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Nothing missing now.</Typography>
      ) : (
        <Stack spacing={0.5}>
          {failing.map((l) => (
            <Stack key={l.lineItemId} direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem', color: 'text.secondary', width: 28 }}>
                L{l.depth}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, minWidth: 140 }}>{l.partNumber}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 130 }}>
                {l.packageType ? PACKAGE_TYPE_LABEL[l.packageType] : ''}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                {l.missingFail.map((req) => (
                  <Chip key={req} size="small" variant="outlined" label={requirementLabel(req)} sx={{ height: 20, fontSize: '0.7rem', color: '#b91c1c', borderColor: '#fca5a5' }} />
                ))}
              </Stack>
            </Stack>
          ))}
          {failingTotal > failing.length && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', pl: 4.5 }}>+{failingTotal - failing.length} more</Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default PackageGapsCard;
